import { useCallback, useEffect } from 'react';
import { ApolloError } from '@apollo/client';

import type { GraphQLError } from 'graphql';

import { NotFoundError } from 'activate-errors';
import type { InspectionTemplate } from 'inspection-types';
import { useCurrentInspectionTemplate } from 'react-hooks';

import type { ReplicaLoadFailure } from '../model/operations';

export type UseTemplateQueryParams = {
  channelId: string | undefined;
  templateId: string | undefined;
};

export type UseTemplateQueryResult = {
  template: InspectionTemplate | undefined;
  loading: boolean;
  failure: ReplicaLoadFailure | undefined;
  refresh: () => Promise<void>;
};

function readExceptionCode(graphQLError: GraphQLError): unknown {
  const exception = graphQLError.extensions?.exception;

  return typeof exception === 'object' &&
    exception !== null &&
    'code' in exception
    ? exception.code
    : undefined;
}

function isTemplateNotFound(error: Error): boolean {
  const { cause } = error;

  return (
    cause instanceof ApolloError &&
    cause.graphQLErrors.some(
      graphQLError =>
        readExceptionCode(graphQLError) === NotFoundError.errorName
    )
  );
}

function toLoadFailure(error: Error): ReplicaLoadFailure {
  return isTemplateNotFound(error) ? 'notFound' : 'error';
}

export function useTemplateQuery({
  channelId,
  templateId,
}: UseTemplateQueryParams): UseTemplateQueryResult {
  const { template, loading, error, fetchCurrentInspectionTemplate } =
    useCurrentInspectionTemplate();

  const refresh = useCallback(async () => {
    if (!channelId || !templateId) {
      return;
    }

    await fetchCurrentInspectionTemplate({
      id: templateId,
      channelId,
      includeHidden: true,
    }).catch(() => undefined);
  }, [channelId, templateId, fetchCurrentInspectionTemplate]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    // The wrapped hook keeps the previous template until the new one resolves
    template: template?.id === templateId ? template : undefined,
    loading,
    failure: error ? toLoadFailure(error) : undefined,
    refresh,
  };
}
